import { useEffect, useState } from 'react';
import { Link } from 'react-admin';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  BALANCE_LABELS,
  balanceLink,
  fetchTeamLeaderBalance,
  type BalanceLink,
  type FleetBalanceRow,
  type RegionBalanceKind,
  type TeamLeaderBalanceData,
} from './ftlDashboardsApi';
import { MOCKUP_CLASS } from '../../mockupLook/tokens';

/**
 * Pulpit Team Leadera: „Bilans floty i postęp planistów” oraz „Deficyt
 * i nadwyżka według kraju załadunku” (prace 24.10.2026, S-24.10-08,
 * przełącznik `teamLeaderBalance`; makieta FTL §8). Liczby to szacunek;
 * każda prowadzi do harmonogramu floty albo listy zleceń z filtrem, którym
 * API ją policzyło (adres z odpowiedzi, jak kafelki). Wiersz „bez
 * opiekuna” nie ma odnośników — harmonogram i lista nie mają takiego filtra.
 */

export const BALANCE_NOTE =
  'Szacunek: zajętość i zwolnienie liczone z okien zleceń i modelu przejazdu, pozycja jak w rekomendacjach. Bez wózków i aut poza planowaniem. Liczba prowadzi do harmonogramu floty albo listy zleceń z tym samym filtrem — liczniki „wolne za …” widać nad osią harmonogramu.';

export const REGIONS_OFF_NOTE =
  'Deficyt i nadwyżka wymagają filtra „Wymagające obsadzenia” na liście zleceń — w tej wersji go nie ma.';

const CHIP_COLOR: Record<RegionBalanceKind, 'error' | 'success' | 'default'> = {
  deficit: 'error',
  surplus: 'success',
  even: 'default',
};

/** Liczba z odnośnikiem (albo bez, gdy ekran nie ma filtra). */
function Count({ value, link, testId }: { value: number | null; link: BalanceLink | null; testId?: string }) {
  if (value === null) return <span data-testid={testId}>—</span>;
  const to = balanceLink(link);
  return to ? (
    <Link to={to} data-testid={testId}>
      {value}
    </Link>
  ) : (
    <span data-testid={testId}>{value}</span>
  );
}

function rowName(r: FleetBalanceRow): string {
  if (r.total) return 'Cała flota';
  return r.caretaker ?? 'Bez opiekuna';
}

function rowKey(r: FleetBalanceRow): string {
  if (r.total) return 'total';
  return r.caretaker ?? 'none';
}

export function TeamLeaderBalance() {
  const [data, setData] = useState<TeamLeaderBalanceData | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    fetchTeamLeaderBalance()
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError('Nie udało się policzyć bilansu floty. Odśwież stronę.');
      });
    return () => {
      active = false;
    };
  }, []);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <CircularProgress size={24} />;

  const hours = data.counterHours.slice(1);
  return (
    <Stack spacing={3} data-testid="tl-balance">
      <Box>
        <Typography variant="h6" gutterBottom className={MOCKUP_CLASS.sectionTitle}>
          Bilans floty i postęp planistów
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="tl-balance-note">
          {BALANCE_NOTE}
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Bilans floty i postęp planistów" data-testid="tl-balance-fleet">
            <TableHead>
              <TableRow>
                <TableCell>Opiekun FTL</TableCell>
                <TableCell align="right">Pojazdy</TableCell>
                <TableCell align="right">Wolne teraz</TableCell>
                {hours.map((h) => (
                  <TableCell key={h} align="right">
                    Wolne za {h} h
                  </TableCell>
                ))}
                <TableCell align="right">Z planem poza jutro</TableCell>
                <TableCell align="right">Warianty czekają</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.fleet.map((r) => {
                const key = rowKey(r);
                return (
                  <TableRow
                    key={key}
                    data-testid={`tl-balance-row-${key}`}
                    sx={r.total ? { '& td': { fontWeight: 600 } } : undefined}
                  >
                    <TableCell>{rowName(r)}</TableCell>
                    <TableCell align="right">
                      <Count value={r.vehicles} link={r.links.vehicles} />
                    </TableCell>
                    <TableCell align="right">
                      <Count value={r.freeAt[0] ?? 0} link={r.links.freeNow} testId={`tl-balance-free-now-${key}`} />
                    </TableCell>
                    {hours.map((h, i) => (
                      <TableCell key={h} align="right">
                        <Count value={r.freeAt[i + 1] ?? 0} link={r.links.later} />
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <Count value={r.plannedBeyondTomorrow} link={r.links.later} />
                    </TableCell>
                    <TableCell align="right">
                      <Count
                        value={r.variantsWaiting}
                        link={r.links.variantsWaiting}
                        testId={`tl-balance-variants-${key}`}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>
      <Box data-testid="tl-balance-regions-section">
        <Typography variant="h6" gutterBottom className={MOCKUP_CLASS.sectionTitle}>
          Deficyt i nadwyżka według kraju załadunku
        </Typography>
        {data.regions === null ? (
          <Alert severity="info">{REGIONS_OFF_NOTE}</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Zlecenia gotowe i wstępnie planowane bez pojazdu z załadunkiem w ciągu {data.horizonHours} h wobec
              pojazdów, które w tym czasie zwalniają się w kraju załadunku albo tam stoją. Horyzont to wartość robocza
              z „Parametrów rekomendacji”.
            </Typography>
            {data.regions.rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Brak zleceń do obsadzenia i pojazdów zwalniających się w tym horyzoncie.
              </Typography>
            ) : (
              <Table size="small" aria-label="Deficyt i nadwyżka według kraju załadunku" data-testid="tl-balance-regions">
                <TableHead>
                  <TableRow>
                    <TableCell>Kraj załadunku</TableCell>
                    <TableCell align="right">Zlecenia bez pojazdu</TableCell>
                    <TableCell align="right">Pojazdy zwalniające się</TableCell>
                    <TableCell>Bilans</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.regions.rows.map((r) => (
                    <TableRow key={r.country} data-testid={`tl-balance-region-${r.country}`}>
                      <TableCell>{r.country}</TableCell>
                      <TableCell align="right">
                        <Count value={r.orders} link={r.ordersLink} testId={`tl-balance-region-orders-${r.country}`} />
                      </TableCell>
                      <TableCell align="right">{r.vehicles}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" color={CHIP_COLOR[r.balance]} label={BALANCE_LABELS[r.balance]} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {(data.regions.ordersWithoutCountry > 0 || data.regions.vehiclesWithoutPlace > 0) && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                Poza tabelą: zlecenia bez kraju załadunku — {data.regions.ordersWithoutCountry}; pojazdy bez znanego
                miejsca zwolnienia — {data.regions.vehiclesWithoutPlace} (kraju nie zgadujemy).
              </Typography>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
}
